// ── 공통 모달 컴포넌트 ────────────────────────────────────────────────────────
// visualViewport 추적으로 모바일 키보드 대응 (iOS Safari)
// dark mode: CSS 변수 토큰 + Tailwind dark: 클래스 사용
import React, { useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../hooks/useOverlayClickClose';

/**
 * Props:
 *   open    — 열림 여부
 *   title   — 제목
 *   icon    — 헤더 아이콘 (이모지 문자열, 선택 — 있으면 흰 반투명 박스에 표시)
 *   onClose — 닫기 콜백
 *   footer  — 하단 버튼 영역 (ReactNode)
 *   width   — 카드 너비 (기본 min(560px, 92vw))
 */
// default width: calc(100vw - 48px) — 모바일 좌우 24px씩 확실한 여백 (이전 92vw는 여백 부족)
function Modal({
  open,
  title,
  icon,
  onClose,
  children,
  footer,
  width = 'min(560px, calc(100vw - 48px))',
  mobileSheet = false,
  mobileSheetDragClose = false,
}) {
  // visualViewport 추적 — 모바일 키보드/zoom 시 height/width/offset 모두 변동 (iOS Safari)
  // (이전: height만 추적 → iOS focus zoom 시 카드가 layout viewport 기준이라 visual viewport 좌우로 빠져나감)
  const [vpHeight,   setVpHeight]   = useState(() => window.visualViewport?.height   ?? window.innerHeight);
  const [vpWidth,    setVpWidth]    = useState(() => window.visualViewport?.width    ?? window.innerWidth);
  const [vpOffsetTop, setVpOffsetTop] = useState(() => window.visualViewport?.offsetTop ?? 0);
  const [vpOffsetLeft, setVpOffsetLeft] = useState(() => window.visualViewport?.offsetLeft ?? 0);
  const [sheetDragY, setSheetDragY] = useState(0);
  const sheetDragStartY = useRef(0);
  const sheetDragYRef = useRef(0);
  const sheetDragging = useRef(false);
  const sheetHandleRef = useRef(null);
  const SHEET_DISMISS_THRESHOLD = 100;

  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      setVpHeight(vv.height);
      setVpWidth(vv.width);
      setVpOffsetTop(vv.offsetTop);
      setVpOffsetLeft(vv.offsetLeft);
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, [open]);

  const isMobileSheet = mobileSheet && vpWidth <= 520;
  const canDragClose = isMobileSheet && mobileSheetDragClose;

  useBodyScrollLock(open);
  // 카드 안에서 드래그 시작 → 오버레이에서 손 뗌 오닫힘 방지 (armed-click)
  const overlayCloseProps = useOverlayClickClose(onClose);

  useEffect(() => {
    if (!open || !canDragClose) return;
    const el = sheetHandleRef.current;
    if (!el) return;

    const onStart = (event) => {
      if (!event.touches?.length) return;
      event.stopPropagation();
      sheetDragging.current = true;
      sheetDragStartY.current = event.touches[0].clientY;
      sheetDragYRef.current = 0;
      setSheetDragY(0);
    };
    const onMove = (event) => {
      if (!sheetDragging.current || !event.touches?.length) return;
      const delta = event.touches[0].clientY - sheetDragStartY.current;
      if (delta > 0) {
        event.stopPropagation();
        if (event.cancelable) event.preventDefault();
        sheetDragYRef.current = delta;
        setSheetDragY(delta);
      }
    };
    const onEnd = (event) => {
      event?.stopPropagation?.();
      if (!sheetDragging.current) return;
      sheetDragging.current = false;
      if (sheetDragYRef.current >= SHEET_DISMISS_THRESHOLD) {
        setSheetDragY(0);
        onClose();
      } else {
        setSheetDragY(0);
      }
      sheetDragYRef.current = 0;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [open, canDragClose, onClose]);

  if (!open) return null;

  // ── 오버레이: visual viewport에 정확히 정렬 (top/left/width/height 모두 동적) ──
  // 이전: left:0 right:0 (layout viewport 기준 → iOS zoom 시 visual viewport 벗어남)
  const overlayPositionStyle = {
    position: 'fixed',
    left: vpOffsetLeft,
    top: vpOffsetTop,
    width: vpWidth,
    height: vpHeight,
    zIndex: 1000,
  };

  // ── 카드: visual viewport 기반 동적 캡 — iOS focus zoom에서도 화면 안 보장 ──
  // 이전: 'calc(100vw - 48px)' 정적 → vw=layout viewport 기준 → zoom 시 visual viewport 벗어남
  // 지금: Math.max(0, vpWidth - 48) → visualViewport 실시간 추적 (zoom/keyboard 모두 대응)
  const cardHeightStyle = {
    maxHeight: vpHeight * 0.9,
    maxWidth: Math.max(0, vpWidth - 48),
  };
  const dragProgress = Math.min(sheetDragY / SHEET_DISMISS_THRESHOLD, 1);
  const cardStyle = isMobileSheet
    ? {
        maxHeight: vpHeight * 0.88,
        maxWidth: vpWidth,
        width: '100%',
        background: 'color-mix(in srgb, var(--bg-card) 86%, var(--accent) 14%)',
        borderRadius: '20px 20px 0 0',
        borderTop: '2px solid rgba(96, 165, 250, 0.72)',
        borderLeft: '1px solid rgba(96, 165, 250, 0.42)',
        borderRight: '1px solid rgba(96, 165, 250, 0.42)',
        boxShadow: '0 -22px 76px rgba(0,0,0,0.82), 0 0 0 1px rgba(125, 211, 252, 0.18)',
      }
    : { ...cardHeightStyle, width };
  const cardDragStyle = canDragClose
    ? {
        transform: `translateY(${sheetDragY}px)`,
        transition: sheetDragging.current ? 'none' : 'transform 0.24s cubic-bezier(0.22,1,0.36,1)',
        opacity: 1 - dragProgress * 0.25,
      }
    : null;

  function stopTouchPropagation(e) {
    e.stopPropagation();
  }

  return (
    <div
      style={overlayPositionStyle}
      className={`flex ${isMobileSheet ? 'items-end' : 'items-center'} justify-center bg-slate-900/45 dark:bg-slate-950/70 backdrop-blur-sm`}
      {...overlayCloseProps}
      onTouchStart={stopTouchPropagation}
      onTouchMove={stopTouchPropagation}
      onTouchEnd={stopTouchPropagation}
      onTouchCancel={stopTouchPropagation}
    >
      {/*
        카드 overflow: 'visible' — native <input type="date"> 등 popup이 카드 영역을 벗어날 수 있도록 허용 (날짜 피커 짤림 방지).
        본문(아래)은 'overflow-y-auto'로 스크롤 처리하므로 카드 자체는 overflow 제약 불필요.
        둥근 모서리는 본문 div가 rounded-2xl을 따라가므로 시각 영향 없음 (스크롤바도 본문 내부).
      */}
      <div
        style={{ ...cardStyle, ...(cardDragStyle || {}) }}
        className="
          flex flex-col
          bg-white dark:bg-[var(--bg-card)]
          rounded-2xl
          border border-[var(--border)]
          shadow-[0_24px_64px_rgba(0,0,0,0.16),0_4px_16px_rgba(0,0,0,0.08)]
          dark:shadow-[0_24px_64px_rgba(0,0,0,0.5),0_4px_16px_rgba(0,0,0,0.3)]
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── 헤더: 검정 밴드 + 아이콘 박스 + 흰 제목 (2026-08-31 모달 통일 — 계약관리 헤더 구성) ── */}
        {isMobileSheet && (
          <div
            ref={sheetHandleRef}
            className="flex items-center justify-center flex-shrink-0"
            style={{
              height: canDragClose ? 44 : 20,
              touchAction: canDragClose ? 'none' : 'auto',
              cursor: canDragClose ? 'grab' : 'default',
              userSelect: 'none',
              background: 'var(--std-thead, #0f172a)',
              borderRadius: '20px 20px 0 0',
            }}
          >
            <span
              className="h-1 rounded-full bg-white/40"
              style={{
                width: canDragClose && sheetDragY > 30 ? 52 : 40,
                transition: 'width 0.15s',
              }}
            />
          </div>
        )}
        <div
          className={`flex items-center justify-between gap-3 ${isMobileSheet ? 'px-4 py-3' : 'px-5 py-[14px]'} flex-shrink-0`}
          style={{ background: 'var(--std-thead, #0f172a)', borderRadius: isMobileSheet ? 0 : '16px 16px 0 0' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            {icon && (
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.18)', borderRadius: 11, fontSize: 17 }}
                aria-hidden="true"
              >
                {icon}
              </div>
            )}
            <span className="text-[15px] font-bold text-white tracking-tight leading-tight">
              {title}
            </span>
          </div>
          <button
            className="
              inline-flex items-center justify-center w-[30px] h-[30px] rounded-[9px]
              text-white bg-white/15 hover:bg-white/25
              border-0 cursor-pointer transition-colors duration-150
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/50
            "
            onClick={onClose}
            aria-label="닫기"
          >
            {/* X 아이콘 */}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── 본문 ── */}
        {/* min-w-0: flex 자식의 콘텐츠 크기 보장이 부모 카드를 늘리는 것 차단 (긴 주소/IP가 있는 폼 대응) */}
        <div className={`${isMobileSheet ? 'px-4 py-3' : 'px-5 py-[18px]'} overflow-y-auto flex-1 min-w-0`}>
          {children}
        </div>

        {/* ── 푸터 ── */}
        {footer && (
          <>
            <div className="h-px bg-[var(--border)] flex-shrink-0" />
            <div className={`${isMobileSheet ? 'px-4 py-3 bg-[var(--bg-subtle)]/60' : 'px-5 py-3'} flex-shrink-0`}>{footer}</div>
          </>
        )}
      </div>
    </div>
  );
}

export default Modal;
